import React from 'react'
import { cn } from '../../lib/utils'

export interface DescriptionListItem {
  term: string
  description: React.ReactNode
  termClassName?: string
  descriptionClassName?: string
}

export interface DescriptionListProps {
  title?: string
  description?: string
  items: DescriptionListItem[]
  className?: string
  headerClassName?: string
  listClassName?: string
}

/**
 * DescriptionList - A card-based description list component for displaying key-value pairs
 *
 * Every className prop is merged with tailwind-merge (`cn`): a caller class that
 * conflicts with a default (e.g. `bg-transparent` vs `bg-card`) replaces it.
 * 
 * @example
 * ```tsx
 * <DescriptionList
 *   title="Organization Information"
 *   description="Basic details and settings for your organization."
 *   items={[
 *     { term: 'Organization Name', description: 'Acme Corporation' },
 *     { term: 'Email', description: 'admin@acme.com' },
 *   ]}
 * />
 * ```
 */
export function DescriptionList({
  title,
  description,
  items,
  className = '',
  headerClassName = '',
  listClassName = '',
}: DescriptionListProps) {
  return (
    <div className={cn('overflow-hidden bg-card border border-border sm:rounded-lg', className)}>
      {(title || description) && (
        <div className={cn('px-4 py-6 sm:px-6', headerClassName)}>
          {title && (
            <h3 className="text-base/7 font-semibold text-foreground">
              {title}
            </h3>
          )}
          {description && (
            <p className="mt-1 max-w-2xl text-sm/6 text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      )}
      <div className={`${title || description ? 'border-t border-border' : ''}`}>
        <dl className={cn('divide-y divide-border', listClassName)}>
          {items.map((item, index) => (
            <div key={index} className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className={cn('text-sm font-medium text-foreground', item.termClassName)}>
                {item.term}
              </dt>
              <dd className={cn('mt-1 text-sm/6 text-foreground/80 sm:col-span-2 sm:mt-0', item.descriptionClassName)}>
                {item.description}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}
